import React, { useRef, useEffect, useState } from "react";

//import headSrc from "./images/head.png";
import tailSrc from "./images/tail_red.png";
import bodySrc from "./images/body_red.png";
import turnSrc from "./images/turn_red.png";
import tailTurnSrc from "./images/tail_turn_red.png";
import foodSrc from "./images/apple.png";
import backgroundSrc from "./images/background_tiles_brighter.png";
import lottie from "lottie-web";
import headAnimationData from "./animations/head.json";

import {
  startSession,
  endSession,
  recordKeystrokes,
} from "../../services/GameService"; // Import your session functions
import LevelAdvanceNotice from "../common/LevelAdvanceNotice";
import { usePlayClock } from "../common/usePlayClock";

// Starting snake (head first), near the left edge heading right, so even the
// smallest level board leaves room to react before the wall.
export const startingSnake = (right = { x: 1, y: 0 }) => [
  { x: 3, y: 5, dir: right }, // Head
  { x: 2, y: 5, dir: right }, // Body segment
  { x: 1, y: 5, dir: right }, // Tail
];

const SnakeGameForm = ({ cellCount = 10, canvasWidth = 400, speed = 200 }) => {
  // Game variables
  const directions = {
    UP: { x: 0, y: -1 },
    DOWN: { x: 0, y: 1 },
    LEFT: { x: -1, y: 0 },
    RIGHT: { x: 1, y: 0 },
  };

  const [images, setImages] = useState({});
  const [imagesLoaded, setImagesLoaded] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [snake, setSnake] = useState([]);
  const [gameCellCount, setGameCellCount] = useState(cellCount);
  const [gameCanvasWidth, setGameCanvasWidth] = useState(canvasWidth);
  const [gameSpeed, setGameSpeed] = useState(speed);
  const [levelNumber, setLevelNumber] = useState(1);

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const lottieCanvasRef = useRef(null);
  const currentDirectionRef = useRef(directions.RIGHT);
  const isTurningRef = useRef(false);
  const lottieRef = useRef(null);
  const foodPosRef = useRef({});
  const sessionIdRef = useRef(null);
  const keystrokesRef = useRef([]);
  const scoreRef = useRef(0);
  const levelRef = useRef(null);
  const gridSizeRef = useRef(canvasWidth / cellCount);
  const playClock = usePlayClock();

  const areDirectionsEqual = (dir1, dir2) =>
    dir1.x === dir2.x && dir1.y === dir2.y;

  const setNewFoodLocation = () => {
    const x_pos = Math.floor(
      Math.random() * (gameCanvasWidth / gridSizeRef.current)
    );
    const y_pos = Math.floor(
      Math.random() * (gameCanvasWidth / gridSizeRef.current)
    );

    return {
      x: x_pos,
      y: y_pos,
    };
  };

  const initializeGame = async () => {
    setSnake(startingSnake(directions.RIGHT));
    currentDirectionRef.current = directions.RIGHT;
    foodPosRef.current = setNewFoodLocation();
    setScore(0);
    scoreRef.current = 0;
    setGameOver(false);
    setIsPaused(false);
    // The snake sets off straight away; there's no Start to wait for.
    playClock.start();

    // Start session and handle session ID
    const sessionParams = await startSession("snake-game", {
      cellCount: cellCount,
      speed: speed,
    });

    const sessionId = sessionParams.sessionId;
    if (sessionId) {
      sessionIdRef.current = sessionId; // Store the session ID if valid
      console.log("Session started with ID:", sessionId);
    }

    levelRef.current = sessionParams.level;
    const params = JSON.parse(levelRef.current.level_parameters);
    setGameCanvasWidth(params.canvasWidth);
    setGameCellCount(params.cellCount);
    setGameSpeed(params.speed);
    setLevelNumber(levelRef.current.level_number);
  };

  useEffect(() => {
    gridSizeRef.current = gameCanvasWidth / gameCellCount;
  }, [gameCanvasWidth, gameCellCount]);

  useEffect(() => {
    initializeGame();
    // Load images
    const loadImages = async () => {
      const imageSources = {
        //        head: headSrc,
        tail: tailSrc,
        body: bodySrc,
        turn: turnSrc,
        tailTurn: tailTurnSrc,
        food: foodSrc,
        background: backgroundSrc,
      };

      const loadedImages = {};
      const imageLoadPromises = Object.entries(imageSources).map(
        ([key, src]) =>
          new Promise((resolve) => {
            const img = new Image();
            img.src = src;
            img.onload = () => {
              loadedImages[key] = img;
              resolve();
            };
          })
      );

      await Promise.all(imageLoadPromises);
      setImages(loadedImages);
      setImagesLoaded(true);
    };

    // Create a canvas for Lottie
    const lottieCanvas = document.createElement("canvas");

    lottieCanvas.width = gridSizeRef.current; // Set to your desired grid size
    lottieCanvas.height = gridSizeRef.current; // Set to your desired grid size

    lottieCanvasRef.current = lottieCanvas; // Assign to the ref
    document.body.appendChild(lottieCanvas); // Optional: Attach it to the DOM for visibility/debugging

    const animation = lottie.loadAnimation({
      container: lottieCanvas,
      renderer: "canvas",
      loop: true,
      autoplay: true,
      animationData: headAnimationData,
    });

    lottieRef.current = animation;

    loadImages();

    const canvas = canvasRef.current;
    ctxRef.current = canvas.getContext("2d");

    return () => {
      if (lottieRef.current) {
        lottieRef.current.destroy(); // Cleanup Lottie instance
      }
    };
  }, []);

  useEffect(() => {
    if (!imagesLoaded) return;

    let gameInterval;

    const drawSnake = () => {
      for (let i = 0; i < snake.length; i++) {
        const segment = snake[i];
        let img = images.body; // Default image for body

        if (i === 0) {
          // Head
          if (i === 0) {
            const lottieCanvas =
              lottieRef.current.renderer.canvasContext.canvas;

            if (lottieCanvas) {
              img = lottieCanvas;
            }
          }
        } else if (i === snake.length - 1) {
          // Tail logic
          const tail = segment;
          const beforeTail = snake[i - 1];
          const prevSegmentDir = beforeTail.dir;
          const tailDir = tail.dir;

          if (!areDirectionsEqual(prevSegmentDir, tailDir)) {
            img = images.tailTurn; // Use tail-turn image
            ctxRef.current.save();
            const x = tail.x * gridSizeRef.current;
            const y = tail.y * gridSizeRef.current;
            ctxRef.current.translate(
              x + gridSizeRef.current / 2,
              y + gridSizeRef.current / 2
            );

            // Rotate based on direction change
            if (
              areDirectionsEqual(prevSegmentDir, directions.RIGHT) &&
              areDirectionsEqual(tailDir, directions.UP)
            ) {
              ctxRef.current.rotate((90 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.UP) &&
              areDirectionsEqual(tailDir, directions.LEFT)
            ) {
              ctxRef.current.rotate(0);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.LEFT) &&
              areDirectionsEqual(tailDir, directions.DOWN)
            ) {
              ctxRef.current.rotate((-90 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.DOWN) &&
              areDirectionsEqual(tailDir, directions.RIGHT)
            ) {
              ctxRef.current.rotate((-180 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.RIGHT) &&
              areDirectionsEqual(tailDir, directions.DOWN)
            ) {
              ctxRef.current.scale(-1, 1); //flip horizontally
              ctxRef.current.rotate((-90 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.DOWN) &&
              areDirectionsEqual(tailDir, directions.LEFT)
            ) {
              ctxRef.current.scale(-1, 1); //flip horizontally
              ctxRef.current.rotate((180 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.LEFT) &&
              areDirectionsEqual(tailDir, directions.UP)
            ) {
              ctxRef.current.scale(-1, 1); //flip horizontally
              ctxRef.current.rotate((90 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.UP) &&
              areDirectionsEqual(tailDir, directions.RIGHT)
            ) {
              ctxRef.current.scale(-1, 1); //flip horizontally
            }

            ctxRef.current.drawImage(
              img,
              -gridSizeRef.current / 2,
              -gridSizeRef.current / 2,
              gridSizeRef.current,
              gridSizeRef.current
            );
            ctxRef.current.restore();
            continue;
          }
          img = images.tail; // Straight tail
        } else {
          // Handle turns for body segments
          const prevSegmentDir = snake[i - 1].dir;
          const currSegmentDir = segment.dir;

          if (!areDirectionsEqual(prevSegmentDir, currSegmentDir)) {
            img = images.turn; // Use turn image
            ctxRef.current.save();
            const x = segment.x * gridSizeRef.current;
            const y = segment.y * gridSizeRef.current;
            ctxRef.current.translate(
              x + gridSizeRef.current / 2,
              y + gridSizeRef.current / 2
            );

            // Rotate based on direction change
            if (
              areDirectionsEqual(prevSegmentDir, directions.RIGHT) &&
              areDirectionsEqual(currSegmentDir, directions.UP)
            ) {
              ctxRef.current.rotate((90 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.UP) &&
              areDirectionsEqual(currSegmentDir, directions.LEFT)
            ) {
              ctxRef.current.rotate(0);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.LEFT) &&
              areDirectionsEqual(currSegmentDir, directions.DOWN)
            ) {
              ctxRef.current.rotate((-90 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.DOWN) &&
              areDirectionsEqual(currSegmentDir, directions.RIGHT)
            ) {
              ctxRef.current.rotate((180 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.RIGHT) &&
              areDirectionsEqual(currSegmentDir, directions.DOWN)
            ) {
              ctxRef.current.rotate(0);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.DOWN) &&
              areDirectionsEqual(currSegmentDir, directions.LEFT)
            ) {
              ctxRef.current.rotate((90 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.LEFT) &&
              areDirectionsEqual(currSegmentDir, directions.UP)
            ) {
              ctxRef.current.rotate((-180 * Math.PI) / 180);
            } else if (
              areDirectionsEqual(prevSegmentDir, directions.UP) &&
              areDirectionsEqual(currSegmentDir, directions.RIGHT)
            ) {
              ctxRef.current.rotate((-90 * Math.PI) / 180);
            }

            ctxRef.current.drawImage(
              img,
              -gridSizeRef.current / 2,
              -gridSizeRef.current / 2,
              gridSizeRef.current,
              gridSizeRef.current
            );
            ctxRef.current.restore();
            continue;
          }
        }

        // Draw regular segment (straight body, head, or tail)
        ctxRef.current.save();
        const x = segment.x * gridSizeRef.current;
        const y = segment.y * gridSizeRef.current;

        if (areDirectionsEqual(segment.dir, directions.RIGHT)) {
          ctxRef.current.translate(
            x + gridSizeRef.current / 2,
            y + gridSizeRef.current / 2
          );
          ctxRef.current.rotate((90 * Math.PI) / 180);
          ctxRef.current.drawImage(
            img,
            -gridSizeRef.current / 2,
            -gridSizeRef.current / 2,
            gridSizeRef.current,
            gridSizeRef.current
          );
        } else if (areDirectionsEqual(segment.dir, directions.LEFT)) {
          ctxRef.current.translate(
            x + gridSizeRef.current / 2,
            y + gridSizeRef.current / 2
          );
          ctxRef.current.rotate((-90 * Math.PI) / 180);
          ctxRef.current.drawImage(
            img,
            -gridSizeRef.current / 2,
            -gridSizeRef.current / 2,
            gridSizeRef.current,
            gridSizeRef.current
          );
        } else if (areDirectionsEqual(segment.dir, directions.DOWN)) {
          ctxRef.current.translate(
            x + gridSizeRef.current / 2,
            y + gridSizeRef.current / 2
          );
          ctxRef.current.rotate((180 * Math.PI) / 180);
          ctxRef.current.drawImage(
            img,
            -gridSizeRef.current / 2,
            -gridSizeRef.current / 2,
            gridSizeRef.current,
            gridSizeRef.current
          );
        } else {
          ctxRef.current.drawImage(
            img,
            x,
            y,
            gridSizeRef.current,
            gridSizeRef.current
          );
        }

        ctxRef.current.restore();
      }
    };

    const drawFood = () => {
      ctxRef.current.drawImage(
        images.food,
        foodPosRef.current.x * gridSizeRef.current,
        foodPosRef.current.y * gridSizeRef.current,
        gridSizeRef.current,
        gridSizeRef.current
      );
    };

    const drawBackground = () => {
      ctxRef.current.drawImage(
        images.background,
        0,
        0,
        gameCanvasWidth,
        gameCanvasWidth
      );
    };

    const moveSnake = () => {
      const head = snake[0];
      const newHead = {
        x: head.x + currentDirectionRef.current.x,
        y: head.y + currentDirectionRef.current.y,
        dir: currentDirectionRef.current,
      };

      snake.unshift(newHead);

      // Check food collision
      if (
        newHead.x === foodPosRef.current.x &&
        newHead.y === foodPosRef.current.y
      ) {
        foodPosRef.current = setNewFoodLocation();
      } else {
        snake.pop();
      }

      // Check collisions
      if (
        newHead.x < 0 ||
        newHead.y < 0 ||
        newHead.x >= gameCanvasWidth / gridSizeRef.current ||
        newHead.y >= gameCanvasWidth / gridSizeRef.current ||
        snake.slice(1).some((seg) => seg.x === newHead.x && seg.y === newHead.y)
      ) {
        clearInterval(gameInterval);
        setGameOver(true);
        if (sessionIdRef.current) {
          recordKeystrokes(sessionIdRef.current, keystrokesRef.current);
          endSession(sessionIdRef.current, scoreRef.current, playClock.stop());
        }
      }

      isTurningRef.current = false;

      if (snake.length - 3 !== score) {
        setScore(snake.length - 3);
        scoreRef.current = snake.length - 3;
      }
    };

    const gameLoop = () => {
      if (isPaused || gameOver) {
        return;
      }
      ctxRef.current.clearRect(0, 0, gameCanvasWidth, gameCanvasWidth);
      drawBackground();
      drawFood();
      moveSnake();
      drawSnake();
    };

    // Use the speed of the level just loaded, so a restart after a promotion
    // or demotion plays at the new level's speed.
    gameInterval = setInterval(gameLoop, gameSpeed);

    return () => clearInterval(gameInterval);
  }, [imagesLoaded, isPaused, gameOver, gameSpeed]);

  const handleKeyDown = (e) => {
    if (isTurningRef.current) return;
    const timestamp = new Date().toISOString(); // Get the current timestamp

    if (
      e.key === "ArrowUp" &&
      !areDirectionsEqual(currentDirectionRef.current, directions.DOWN)
    ) {
      currentDirectionRef.current = directions.UP;
      isTurningRef.current = true;
    } else if (
      e.key === "ArrowDown" &&
      !areDirectionsEqual(currentDirectionRef.current, directions.UP)
    ) {
      currentDirectionRef.current = directions.DOWN;
      isTurningRef.current = true;
    } else if (
      e.key === "ArrowLeft" &&
      !areDirectionsEqual(currentDirectionRef.current, directions.RIGHT)
    ) {
      currentDirectionRef.current = directions.LEFT;
      isTurningRef.current = true;
    } else if (
      e.key === "ArrowRight" &&
      !areDirectionsEqual(currentDirectionRef.current, directions.LEFT)
    ) {
      currentDirectionRef.current = directions.RIGHT;
      isTurningRef.current = true;
    }

    keystrokesRef.current = [
      ...keystrokesRef.current,
      { key: e.key, timestamp },
    ];
  };

  const togglePause = () => {
    if (isPaused) playClock.resume();
    else playClock.pause();
    setIsPaused(!isPaused);
  };

  const restartGame = () => {
    initializeGame();
    setGameOver(false);
  };

  useEffect(() => {
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  return (
    <div>
      <h2 style={{ textAlign: "center" }}>Level Number: {levelNumber}</h2>
      <canvas
        ref={canvasRef}
        width={gameCanvasWidth}
        height={gameCanvasWidth}
        style={{ background: "#111", display: "block", margin: "0 auto" }}
      />
      {!gameOver && (
        <button
          onClick={togglePause}
          style={{ display: "block", margin: "10px auto" }}
        >
          {isPaused ? "Resume" : "Pause"}
        </button>
      )}

      {gameOver && (
        <div>
          <div style={{ textAlign: "center", color: "red" }}>Game Over!</div>
          <button onClick={restartGame}>Restart</button>
          <LevelAdvanceNotice />
        </div>
      )}
      <h2 style={{ textAlign: "center" }}>Score: {score}</h2>
    </div>
  );
};

export default SnakeGameForm;
